import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";
import type { FlowConnectorConfig, FlowResponse, FlowStepConfig } from "@/api";
import { createFlowMutation, getFlowQueryKey, listFlowsQueryKey, schemas, updateFlowMutation } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";

const update = schemas.zFlowUpdate.shape;
const flowFormSchema = z.object({
  name: update.name.unwrap().unwrap(),
  description: update.description,
  purpose: update.purpose,
});
type FlowFormValues = z.infer<typeof flowFormSchema>;

/** A Build with AI draft opened in the create form ("Open in form" on its card). */
export interface FlowFormState {
  draft: {
    name?: string;
    description?: string;
    purpose?: string;
    steps: FlowStepConfig[];
    connectors: FlowConnectorConfig[];
  };
  /** The chat the draft came from; the created workflow is linked to it. */
  sourceSessionUuid?: string;
}

/** The workflow details form — the create page and the Overview tab share it. */
export function FlowForm({ flow }: Readonly<{ flow?: FlowResponse }>) {
  const { t } = useTranslation("workflows");
  const navigate = useNavigate();
  const fromChat = (useLocation().state as FlowFormState | null) ?? undefined;
  const seed = flow ? undefined : fromChat?.draft;
  const text = formText(t, t);
  const { saveExisting, createNew } = useEntitySave(text.messages);

  const create = useMutation(createFlowMutation());
  const updateMutation = useMutation(updateFlowMutation());

  const form = useAppForm({
    defaultValues: {
      name: flow?.name ?? seed?.name ?? "",
      description: flow?.description ?? seed?.description ?? "",
      purpose: flow?.purpose ?? seed?.purpose ?? "",
    } as FlowFormValues,
    validators: { onChange: flowFormSchema },
    onSubmit: ({ value }) => {
      const base = {
        name: value.name,
        description: value.description || null,
        purpose: value.purpose || null,
      };
      if (flow) {
        return saveExisting({
          request: () => updateMutation.mutateAsync({ path: { flow_uuid: flow.uuid }, body: base, throwOnError: true }),
          invalidate: [listFlowsQueryKey(), getFlowQueryKey({ path: { flow_uuid: flow.uuid } })],
          onSaved: () => form.reset(value),
        });
      }
      return createNew({
        request: async () =>
          (
            await create.mutateAsync({
              body: {
                ...base,
                // From a draft, its steps come along; otherwise they're built on the Steps tab.
                steps: seed?.steps ?? [],
                connectors: seed?.connectors ?? [],
                sourceSessionUuid: fromChat?.sourceSessionUuid,
              },
              throwOnError: true,
            })
          ).data.uuid,
        invalidate: [listFlowsQueryKey()],
        // New workflows land on the Steps tab to start building.
        to: (uuid) => `/build/workflows/${uuid}/steps`,
      });
    },
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={text.labels} />
      <form.AppField name="purpose">
        {(field) => (
          <field.TextareaField label={t("form.purpose")} placeholder={t("form.purposePlaceholder")} rows={3} />
        )}
      </form.AppField>
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => (flow ? form.reset() : navigate("/build/workflows"))}
          submitLabel={flow ? undefined : t("form.create")}
          requireChanges={Boolean(flow)}
        />
      </form.AppForm>
    </EntityFormCard>
  );
}
